import { useState, useEffect, useRef, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronsUpDown, Plus, Sun, Moon, Store, ChevronRight, ExternalLink } from 'lucide-react'
import { ASSETS } from '@swim/config'
import { getWorkspaceColor } from '@swim/ui'
import { useTheme } from '../../providers/ThemeProvider'
import { useSession } from '@/hooks/use-session'
import { useMyStreams } from '@/hooks/use-my-streams'
import { useLocaleNavigate } from '@/hooks/use-locale-navigate'

/**
 * LogoMenu — top-left chrome element.
 *
 * Hover reveals "Swim" wordmark + ⇅ chevrons (GSAP animated, width: 64 → 132px).
 * Click opens a workspace switcher flyout anchored to the BUTTON'S RIGHT EDGE
 * in viewport coordinates (position: fixed) — the logo is NEVER obscured.
 */


function WorkspaceAvatar({ initials, colorClass, avatarUrl }: { initials: string; colorClass: string; avatarUrl?: string | null }) {
  const [imgError, setImgError] = useState(false)
  return (
    <div className={`w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center overflow-hidden ${colorClass}`}>
      {avatarUrl && !imgError ? (
        <img src={avatarUrl} alt="" className="w-full h-full object-cover rounded-full" onError={() => setImgError(true)} />
      ) : (
        <span className="text-[9px] font-bold font-sans text-white leading-none select-none">
          {initials}
        </span>
      )}
    </div>
  )
}

interface WorkspaceTipProps {
  open: boolean
  docsUrl: string
  onEnter: () => void
  onLeave: () => void
  label: string
  learnMore: string
}

function WorkspaceTip({ open, docsUrl, onEnter, onLeave, learnMore }: WorkspaceTipProps) {
  const { t } = useTranslation('navigation')
  if (!open) return null
  return (
    <div
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      className="
        absolute top-0 left-full ml-2 z-[60]
        w-56 p-3 rounded-xl
        bg-white dark:bg-slate-800
        border border-sand-200 dark:border-slate-700
        shadow-lg text-left
      "
    >
      <p className="text-xs font-sans text-slate-700 dark:text-slate-300 leading-relaxed">
        A <span className="font-semibold">{t('chrome.workspace.description')}</span> {t('chrome.workspace.descriptionText')}
      </p>
      <a
        href={`${docsUrl}/concepts/workspace/`}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-2 inline-block text-[11px] font-medium text-ocean-500 hover:text-ocean-600 dark:text-ocean-400 dark:hover:text-ocean-300 transition-colors"
      >
        {learnMore}
      </a>
    </div>
  )
}

interface WorkspaceItemProps {
  ws: { id: string; name: string; avatarUrl?: string | null }
  streams: Array<{ id: string; name: string; slug: string; status: 'LIVE' | 'INACTIVE' }>
  isActive: boolean
  isExpanded: boolean
  onSelect: (id: string) => void
  onToggleExpand: (id: string) => void
  onStreamClick: (slug: string) => void
}

function WorkspaceItem({
  ws, streams, isActive, isExpanded, onSelect, onToggleExpand, onStreamClick,
}: WorkspaceItemProps) {
  const { t } = useTranslation('navigation')
  const initials = ws.name.split(' ').map((w) => w[0] ?? '').join('').toUpperCase().slice(0, 2)
  const colorClass = getWorkspaceColor(ws.id)

  return (
    <div>
      <button
        role="menuitem"
        title={ws.name}
        aria-expanded={isExpanded}
        onClick={() => { onSelect(ws.id); if (streams.length > 0) onToggleExpand(ws.id) }}
        className={`
          w-full flex items-center gap-2.5 px-3 py-2
          text-sm font-sans transition-colors text-left
          ${isActive
            ? 'bg-sand-100 dark:bg-slate-700 text-slate-900 dark:text-white'
            : 'text-slate-600 dark:text-slate-300 hover:bg-sand-100 dark:hover:bg-slate-700 hover:text-slate-900 dark:hover:text-white'
          }
        `}
      >
        <WorkspaceAvatar initials={initials} colorClass={colorClass} avatarUrl={ws.avatarUrl} />
        <span className="flex-1 truncate">{ws.name}</span>
        {streams.length > 0 && (
          <ChevronRight
            aria-hidden
            className={`w-3.5 h-3.5 flex-shrink-0 text-slate-400 dark:text-slate-500 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`}
          />
        )}
      </button>

      {/* CSS grid trick: grid-rows 0fr→1fr animates height 0→auto without JS */}
      <div
        style={{
          display: 'grid',
          gridTemplateRows: isExpanded && streams.length > 0 ? '1fr' : '0fr',
          transition: 'grid-template-rows 0.2s ease',
        }}
      >
        <div className="overflow-hidden">
          <div className="pb-1">
            {streams.map((stream) => (
              <button
                key={stream.id}
                role="menuitem"
                onClick={() => onStreamClick(stream.slug)}
                className={`
                  w-full flex items-center gap-2 pl-10 pr-3 py-1.5
                  text-xs font-sans
                  ${stream.status === 'INACTIVE' ? 'opacity-50' : ''}
                  text-slate-500 dark:text-slate-400
                  hover:bg-sand-100 dark:hover:bg-slate-700
                  hover:text-slate-800 dark:hover:text-slate-200
                  transition-colors
                `}
              >
                <img src={ASSETS.nav.streams} alt="" aria-hidden className="w-3 h-3 flex-shrink-0 opacity-60 dark:invert" />
                <span className="truncate">{stream.name}</span>
              </button>
            ))}
            {streams.length > 0 && streams.every(s => s.status === 'INACTIVE') && (
              <p className="px-3 pt-1 pb-2 text-xs text-slate-400 dark:text-slate-500">
                {t('chrome.workspace.activateLabel')}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

// Collapsed: icon 32 + padding 20 + gap 6 + chevron slot 14 (0-opacity), text-crop wrapper at 0 width = ~64 visually
const COLLAPSED_W = 64
// Expanded: icon 32 + gap 6 + text-crop wrapper 78.1 (hardcoded, see below) + gap 6 + chevron 14 + padding 20 = 156.1, +4 safety margin
const EXPANDED_W  = 160

export function LogoMenu() {
  const [open, setOpen]             = useState(false)
  const [hovered, setHovered]       = useState(false)
  const [activeId, setActiveId]     = useState<string | null>(null)
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [flyoutPos, setFlyoutPos]   = useState({ top: 0, left: 0 })
  const [focusedIndex, setFocusedIndex] = useState<number>(-1)

  const [wsTipOpen, setWsTipOpen] = useState(false)
  const wsTipTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const openWsTip  = useCallback(() => {
    if (wsTipTimer.current) clearTimeout(wsTipTimer.current)
    setWsTipOpen(true)
  }, [])
  const closeWsTip = useCallback(() => {
    wsTipTimer.current = setTimeout(() => setWsTipOpen(false), 120)
  }, [])

  const triggerRef  = useRef<HTMLButtonElement>(null)
  const panelRef    = useRef<HTMLDivElement>(null)
  const menuItemsRef = useRef<HTMLElement[]>([])
  const { theme, toggleTheme } = useTheme()
  const { user, isMerchant } = useSession()
  const { t } = useTranslation('navigation')
  const { data: myStreamsData } = useMyStreams()
  const { navigate } = useLocaleNavigate()

  const merchantPortalUrl = (import.meta as ImportMeta & { env: Record<string, string> }).env.VITE_MERCHANT_PORTAL_URL
    ?? 'https://merchant-staging.swimflows.com'

  const workspaces = (myStreamsData?.workspaces ?? []).map((ws) => ({
    id: ws.id,
    name: ws.businessName,
    avatarUrl: ws.avatarUrl,
    streams: ws.streams,
  }))

  const effectiveActiveId = activeId ?? workspaces[0]?.id ?? null

  // Collect all focusable menu items (role="menuitem") whenever menu state changes
  useEffect(() => {
    if (open && panelRef.current) {
      const items = Array.from(panelRef.current.querySelectorAll('[role="menuitem"]')) as HTMLElement[]
      menuItemsRef.current = items
      // Auto-focus first item when menu opens
      if (items.length > 0) {
        setFocusedIndex(0)
        items[0]?.focus()
      }
    } else {
      menuItemsRef.current = []
      setFocusedIndex(-1)
    }
  }, [open, workspaces.length, expandedId, theme])

  // Update tabIndex on all menu items based on focusedIndex
  useEffect(() => {
    menuItemsRef.current.forEach((item, index) => {
      if (index === focusedIndex) {
        item.setAttribute('tabindex', '0')
        item.style.outlineOffset = '2px'
      } else {
        item.setAttribute('tabindex', '-1')
        item.style.outlineOffset = ''
      }
    })
  }, [focusedIndex])

  // Reset focus index when menu opens/closes
  useEffect(() => {
    if (!open) {
      setFocusedIndex(-1)
    }
  }, [open])

  // Menu keyboard navigation (Arrow keys, Home, End, Enter, Escape)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleMenuKeydown = useCallback((e: KeyboardEvent) => {
    if (!open) return

    const itemCount = menuItemsRef.current.length
    const currentFocusedIndex = focusedIndex

    switch (e.key) {
      case 'ArrowDown': {
        e.preventDefault()
        setFocusedIndex((prev) => {
          const next = prev < itemCount - 1 ? prev + 1 : 0
          menuItemsRef.current[next]?.focus()
          return next
        })
        break
      }
      case 'ArrowUp': {
        e.preventDefault()
        setFocusedIndex((prev) => {
          const next = prev > 0 ? prev - 1 : itemCount - 1
          menuItemsRef.current[next]?.focus()
          return next
        })
        break
      }
      case 'Home': {
        e.preventDefault()
        if (itemCount > 0) {
          setFocusedIndex(0)
          menuItemsRef.current[0]?.focus()
        }
        break
      }
      case 'End': {
        e.preventDefault()
        if (itemCount > 0) {
          setFocusedIndex(itemCount - 1)
          menuItemsRef.current[itemCount - 1]?.focus()
        }
        break
      }
      case 'Enter':
      case ' ': {
        if (currentFocusedIndex >= 0 && menuItemsRef.current[currentFocusedIndex]) {
          e.preventDefault()
          menuItemsRef.current[currentFocusedIndex]?.click()
        }
        break
      }
      case 'Escape': {
        e.preventDefault()
        setOpen(false)
        // Return focus to trigger button
        triggerRef.current?.focus()
        break
      }
      default:
        break
    }
  }, [open, focusedIndex])

  useEffect(() => {
    if (open) {
      panelRef.current?.addEventListener('keydown', handleMenuKeydown)
      return () => panelRef.current?.removeEventListener('keydown', handleMenuKeydown)
    }
  }, [open, handleMenuKeydown])

  // ESC closes (global handler as backup)
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) {
        setOpen(false)
        triggerRef.current?.focus()
      }
    }
    if (open) {
      window.addEventListener('keydown', handler)
      return () => window.removeEventListener('keydown', handler)
    }
  }, [open])

  // Close on click outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const inTrigger = triggerRef.current?.contains(e.target as Node)
      const inPanel   = panelRef.current?.contains(e.target as Node)
      if (!inTrigger && !inPanel) setOpen(false)
    }
    if (open) document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  function handleMouseEnter() { setHovered(true) }
  function handleMouseLeave() { setHovered(false) }

  function handleToggle() {
    if (!open && triggerRef.current) {
      // Use EXPANDED_W for anchor — button will animate there regardless of current width
      const rect = triggerRef.current.getBoundingClientRect()
      setFlyoutPos({ top: rect.top, left: rect.left + EXPANDED_W + 8 })
    }
    setHovered(true)
    const newOpen = !open
    setOpen(newOpen)
    // If opening, set focus to first item after menu renders
    if (newOpen) {
      // Focus will be set in the next render via the keyboard handler
    }
  }

  return (
    <div className="relative">

      {/* ── Logo trigger button ─────────────────────────────────────────────── */}
      <button
        ref={triggerRef}
        onClick={handleToggle}
        onKeyDown={(e) => {
          if ((e.key === 'Enter' || e.key === ' ') && !open) {
            e.preventDefault()
            handleToggle()
            // Focus first menu item after menu opens (will be handled by useEffect)
          }
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        aria-label={t('chrome.workspace.switcherAriaLabel')}
        aria-expanded={open}
        style={{
          width: open || hovered ? EXPANDED_W : COLLAPSED_W,
          overflow: 'hidden',
          transition: 'width 0.25s cubic-bezier(0.4,0,0.2,1)',
        }}
        className={`
          flex items-center gap-1.5
          h-10 pl-2 pr-3 rounded-full
          transition-colors duration-200
          text-slate-500 dark:text-slate-400
          ${open || hovered
            ? 'bg-sand-300/60 dark:bg-sand-200/10'
            : 'hover:bg-sand-300/60 dark:hover:bg-sand-200/10'
          }
        `}
      >
        {/* Icon — one element, one size, always rendered, NEVER swapped and
            NEVER resized between collapsed/expanded/hover. This is the part
            that must never shift by even one pixel. */}
        <img
          src={ASSETS.logos.icon.black.md}
          alt={t('chrome.logo.alt')}
          className="w-8 h-8 dark:hidden flex-shrink-0"
        />
        <img
          src={ASSETS.logos.icon.white.md}
          alt={t('chrome.logo.alt')}
          className="w-8 h-8 hidden dark:block flex-shrink-0"
        />

        {/* Wordmark reveal — crop-and-reveal, not swap-and-resize. The image
            itself (ASSETS.logos.horizontal, real logo with letters baked in)
            is hardcoded at a fixed size and fixed position at all times —
            it never animates. Only this wrapper's WIDTH animates, acting as
            a viewport (overflow:hidden) that reveals more of the same
            static image as it widens. The image never moves, never
            resizes — the crop window just gets bigger.
            Sized at height 20 (NOT matched to the 32px icon — a wordmark
            sits smaller than its mark in a normal logo lockup; this matches
            the original text-lg wordmark's visual weight instead). Geometry
            hardcoded from the real SVG's own viewBox (643×110.815, icon
            portion = left 210px of that viewBox): at h=20, full image =
            116px wide, icon portion = 37.9px, so the text-only portion =
            78.1px. Image shifted left by -37.9px (fixed, never changes) so
            only the "WIM" text past the icon is ever visible here. */}
        <div
          className="relative overflow-hidden flex-shrink-0 self-center"
          style={{
            width: open || hovered ? 78.1 : 0,
            height: 20,
            transition: 'width 0.25s cubic-bezier(0.4,0,0.2,1)',
          }}
        >
          <img
            src={ASSETS.logos.horizontal.black.md}
            alt=""
            aria-hidden
            className="absolute top-0 dark:hidden"
            style={{ left: -37.9, height: 20, width: 116, maxWidth: 'none' }}
          />
          <img
            src={ASSETS.logos.horizontal.white.md}
            alt=""
            aria-hidden
            className="absolute top-0 hidden dark:block"
            style={{ left: -37.9, height: 20, width: 116, maxWidth: 'none' }}
          />
        </div>

        {/* ⇅ double chevron */}
        <span
          style={{
            opacity: open || hovered ? 1 : 0,
            transition: 'opacity 0.2s ease 0.08s',
          }}
          className="w-[14px] flex-shrink-0 overflow-hidden flex items-center"
          aria-hidden
        >
          <ChevronsUpDown className="w-3.5 h-3.5" />
        </span>
      </button>

      {/* ── Workspace flyout ─────────────────────────────────────────────────── */}
      {open && (
        <div
          ref={panelRef}
          role="menu"
          className="
            fixed z-50
            min-w-[260px] py-1.5 rounded-xl
            bg-white dark:bg-slate-800
            border border-sand-300 dark:border-slate-700
            shadow-md
          "
          style={{ top: flyoutPos.top, left: flyoutPos.left }}
        >
          {/* ── Workspaces — authenticated only ────────────────────────────── */}
          {user && (
            <>
              <div className="flex items-center gap-1.5 px-3 pt-1.5 pb-1">
                <span className="text-[9px] font-mono tracking-widest uppercase text-slate-400 dark:text-slate-500">
                  {t('chrome.workspace.label')}
                </span>

                <div className="relative">
                  <button
                    type="button"
                    aria-label={t('chrome.workspace.whatIsThis')}
                    onMouseEnter={openWsTip}
                    onMouseLeave={closeWsTip}
                    className="
                      w-3.5 h-3.5 rounded-full flex-shrink-0
                      border border-slate-300 dark:border-slate-600
                      flex items-center justify-center
                      text-[8px] font-bold leading-none
                      text-slate-400 dark:text-slate-500
                      hover:border-slate-400 dark:hover:border-slate-500
                      hover:text-slate-500 dark:hover:text-slate-400
                      transition-colors
                    "
                  >
                    ?
                  </button>
                  <WorkspaceTip
                    open={wsTipOpen}
                    docsUrl={(import.meta as ImportMeta & { env: Record<string, string> }).env.VITE_DOCS_URL ?? 'https://docs.swimflows.com'}
                    onEnter={openWsTip}
                    onLeave={closeWsTip}
                    label={t('chrome.workspace.whatIsThis')}
                    learnMore={t('chrome.workspace.learnMore')}
                  />
                </div>
              </div>

              {workspaces.length > 0 ? workspaces.map((ws) => (
                <WorkspaceItem
                  key={ws.id}
                  ws={ws}
                  streams={ws.streams}
                  isActive={effectiveActiveId === ws.id}
                  isExpanded={expandedId === ws.id}
                  onSelect={(id) => setActiveId(id)}
                  onToggleExpand={(id) => setExpandedId(expandedId === id ? null : id)}
                  onStreamClick={(slug) => { setOpen(false); navigate(`/streams/${slug}`) }}
                />
              )) : (
                <p className="px-3 py-2 text-xs text-slate-400 dark:text-slate-500 font-sans">
                  {isMerchant ? t('chrome.workspace.loading') : t('chrome.workspace.noWorkspaces')}
                </p>
              )}

              <div className="mx-3 my-1 border-t border-sand-200 dark:border-slate-700" />

              <a
                href={`${merchantPortalUrl}/workspaces/new`}
                target="_blank"
                rel="noopener noreferrer"
                role="menuitem"
                onClick={() => setOpen(false)}
                className="
                  w-full flex items-center gap-2.5 px-3 py-2
                  text-sm font-sans
                  text-slate-500 dark:text-slate-400
                  hover:bg-sand-100 dark:hover:bg-slate-700
                  hover:text-slate-900 dark:hover:text-white
                  transition-colors
                "
              >
                <div className="
                  w-6 h-6 rounded-full flex-shrink-0
                  border border-dashed border-slate-300 dark:border-slate-600
                  flex items-center justify-center
                ">
                  <Plus className="w-3 h-3" aria-hidden />
                </div>
                <span>{t('chrome.workspace.addWorkspace')}</span>
              </a>

              {workspaces.length > 0 && (
                <>
                  <div className="mx-3 my-1 border-t border-sand-200 dark:border-slate-700" />

                  <a
                    href={merchantPortalUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    role="menuitem"
                    onClick={() => setOpen(false)}
                    className="
                      w-full flex items-center gap-2.5 px-3 py-2
                      text-sm font-sans font-medium
                      text-ocean-500 dark:text-ocean-400
                      hover:bg-sand-100 dark:hover:bg-slate-700
                      transition-colors
                    "
                  >
                    <div className="w-6 h-6 flex-shrink-0 flex items-center justify-center">
                      <Store className="w-4 h-4" aria-hidden />
                    </div>
                    <span>{t('chrome.workspace.merchantPortal')}</span>
                    <ExternalLink className="w-3 h-3 flex-shrink-0 ml-auto opacity-50" aria-hidden />
                  </a>
                </>
              )}

              <div className="mx-3 my-1 border-t border-sand-200 dark:border-slate-700" />
            </>
          )}

          {/* ── Theme toggle ─────────────────────────────────────────────────── */}
          <div className="flex items-center gap-1 px-2 py-1.5">
            <button
              role="menuitem"
              onClick={() => { if (theme !== 'light') toggleTheme() }}
              aria-label={t('chrome.switchToLight')}
              className={`flex-1 flex items-center justify-center gap-1.5 h-7 rounded-full text-xs transition-colors ${
                theme === 'light'
                  ? 'bg-ocean-100 dark:bg-ocean-800/40 text-ocean-700 dark:text-ocean-300 font-medium'
                  : 'text-slate-500 hover:bg-sand-100 dark:hover:bg-slate-700'
              }`}
            >
              <Sun className="w-3.5 h-3.5" aria-hidden />
              {t('chrome.themeLight')}
            </button>
            <button
              role="menuitem"
              onClick={() => { if (theme !== 'dark') toggleTheme() }}
              aria-label={t('chrome.switchToDark')}
              className={`flex-1 flex items-center justify-center gap-1.5 h-7 rounded-full text-xs transition-colors ${
                theme === 'dark'
                  ? 'bg-ocean-100 dark:bg-ocean-800/40 text-ocean-700 dark:text-ocean-300 font-medium'
                  : 'text-slate-500 hover:bg-sand-100 dark:hover:bg-slate-700'
              }`}
            >
              <Moon className="w-3.5 h-3.5" aria-hidden />
              {t('chrome.themeDark')}
            </button>
          </div>

        </div>
      )}
    </div>
  )
}
